import { ArrowRight, LucideIcon } from "lucide-react";
import React, { ReactNode, useState } from "react";

import {
  IgBadge,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";

interface ScratchCardProps {
  card: {
    id?: string;
    title: ReactNode;
    description?: ReactNode;
    iconComponent: LucideIcon;
    iconColor?: string;
    iconBackgroundColor?: string;
    backgroundColor?: string;
    disabled?: boolean;
    tooltipText?: ReactNode;
    comingSoon?: boolean;
  };
  onClick: () => void;
  isSelected?: boolean;
  renderLocation?:
    | "offers"
    | "overview"
    | "checkout"
    | "noOffers"
    | "noExperiments"
    | "noCheckout"
    | "postPurchase"
    | "noPostPurchase"
    | "offerModsDialog";
}

export const ScratchCard = ({
  card,
  onClick,
  isSelected = false,
  renderLocation,
}: ScratchCardProps) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const showHoverState = isHovered && !card.disabled;

  const content = (
    <IgPaper
      isClickable={!card.disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderRadius: "6px",
        padding:
          renderLocation === "offers" ||
          renderLocation === "checkout" ||
          renderLocation === "postPurchase" ||
          renderLocation === "overview" ||
          renderLocation === "offerModsDialog"
            ? 4
            : 5,
        backgroundColor: showHoverState
          ? `${card.backgroundColor} !important`
          : isSelected && !card.disabled
            ? `${theme.igBackgroundColors.accent} !important`
            : card.disabled
              ? `${theme.igBackgroundColors.interactive.neutral.disabled} !important`
              : undefined,
        border:
          isSelected && !card.disabled
            ? `1px solid ${theme.igBorders.accent.active}`
            : card.disabled
              ? `1px solid ${theme.igBorders.core.disabled}`
              : undefined,
        ...(isSelected &&
          !card.disabled && {
            "&:hover": {
              backgroundColor: card.backgroundColor,
              borderColor: theme.igBorders.accent.active,
            },
          }),
        ...(card.disabled && {
          cursor: "default",
          "&:hover": {
            backgroundColor:
              theme.igBackgroundColors.interactive.neutral.disabled,
            borderColor: theme.igBorders.core.disabled,
          },
        }),
        height:
          renderLocation === "offerModsDialog"
            ? "120px"
            : renderLocation === "offers" ||
                renderLocation === "checkout" ||
                renderLocation === "postPurchase" ||
                renderLocation === "overview"
              ? "100px"
              : "112px",
        overflow: "hidden",
      }}
    >
      <Grid
        container
        direction={"column"}
        mt={0}
        gap={
          renderLocation === "offers" ||
          renderLocation === "checkout" ||
          renderLocation === "postPurchase" ||
          renderLocation === "overview" ||
          renderLocation === "offerModsDialog"
            ? 2
            : 3
        }
      >
        <Grid
          sx={{
            backgroundColor:
              card.iconBackgroundColor ||
              theme.igBackgroundColors.interactive.neutral.default,
            ...(card.disabled && {
              opacity: 0.5,
            }),
            padding: 2,
            width: "40px",
            height: "40px",
            borderRadius: 1,
            transition: "opacity 200ms ease-in, transform 200ms ease-in",
            opacity: showHoverState ? 0 : 1,
            transform: showHoverState ? "translateY(-60px)" : "translateY(0)",
          }}
        >
          <card.iconComponent size={24} color={card.iconColor} />
        </Grid>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          sx={{
            transition: "transform 200ms ease-in",
            transform: showHoverState ? "translateY(-48px)" : "translateY(0)",
          }}
        >
          <Grid size="grow">
            <Grid container direction={"column"} rowSpacing={1}>
              <Grid container direction="row" alignItems="center" gap={2}>
                <IgTypography
                  color={
                    card.disabled
                      ? theme.palette.text.interactive.neutral.disabled
                      : showHoverState
                        ? card.iconColor || theme.palette.text.primary
                        : theme.palette.text.primary
                  }
                  sx={{
                    fontWeight: showHoverState ? 600 : 400,
                  }}
                >
                  {card.title}
                </IgTypography>
                {card.comingSoon && (
                  <IgBadge
                    badgeContent="Coming soon"
                    sx={{
                      "& .MuiBadge-badge": {
                        position: "relative",
                        transform: "none",
                        fontSize: "11px",
                        height: "18px",
                        minWidth: "33px",
                        padding: "2px 4px 2px 4px",
                        borderRadius: 1,
                        backgroundColor: theme.igBackgroundColors.accent,
                        color: theme.palette.text.accent,
                        marginLeft: 1,
                      },
                    }}
                  />
                )}
              </Grid>
              {showHoverState && (
                <Grid
                  sx={{
                    visibility: showHoverState ? "visible" : "hidden",
                    animation: showHoverState
                      ? "fadeInUpDescription 200ms ease-in"
                      : "none",
                    "@keyframes fadeInUpDescription": {
                      from: {
                        opacity: 0,
                        transform: "translateY(8px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  <IgTypography variant={"body2"} sx={{ display: "block" }}>
                    {card.description}
                  </IgTypography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid sx={{ visibility: showHoverState ? "visible" : "hidden" }}>
            <ArrowRight size={24} color={card.iconColor} />
          </Grid>
        </Grid>
      </Grid>
    </IgPaper>
  );

  return (
    <Grid
      container
      direction={"column"}
      key={card.id}
      onClick={card.disabled ? undefined : onClick}
    >
      {card.disabled ? (
        <IgTooltip title={card.tooltipText}>{content}</IgTooltip>
      ) : (
        content
      )}
    </Grid>
  );
};
